import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, MenuItem, Grid, Paper, Typography } from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OmsFormDialog } from './OmsFormDialog';
import { OmsDetailDialog } from './OmsDetailDialog';
import { omsApi, type Oms, type CreateOmsInput, type OmsStatus } from '../../api/oms.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const STATUSES: OmsStatus[] = ['draft', 'pending_approval', 'active', 'on_hold', 'completed', 'closed', 'cancelled'];
const STATUS_COLOR: Record<OmsStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default', pending_approval: 'warning', active: 'info', on_hold: 'warning', completed: 'success', closed: 'default', cancelled: 'error',
};

export function OmsListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Oms | null>(null);
  const [detail, setDetail] = useState<Oms | null>(null);
  const [deleting, setDeleting] = useState<Oms | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dashboard = useQuery({ queryKey: ['oms-dashboard'], queryFn: omsApi.dashboard });
  const { data, isLoading } = useQuery({
    queryKey: ['oms', page, limit, search, status],
    queryFn: () => omsApi.list({ page, limit, search: search || undefined, status: status || undefined }),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['oms'] }); qc.invalidateQueries({ queryKey: ['oms-dashboard'] }); };

  const createMut = useMutation({
    mutationFn: (input: CreateOmsInput) => omsApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateOmsInput }) => {
      const { omsNumber: _omsNumber, ...rest } = input;
      return omsApi.update(id, rest);
    },
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => omsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Oms>[] = [
    { key: 'omsNumber', label: 'OMS ID', width: 160 },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} /> },
    { key: 'isLocked', label: 'Locked', render: (r) => (r.isLocked ? <Chip size="small" color="warning" label="locked" /> : '—') },
  ];

  return (
    <>
      <PageHeader
        title="OMS — Operations Master"
        action={hasPermission('oms.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New OMS</Button>}
      />

      <Grid container spacing={1} mb={2}>
        <Grid item><Paper sx={{ p: 1.5, minWidth: 90, textAlign: 'center' }}><Typography variant="h6">{dashboard.data?.total ?? 0}</Typography><Typography variant="caption">Total</Typography></Paper></Grid>
        {STATUSES.map((s) => (
          <Grid item key={s}>
            <Paper sx={{ p: 1.5, minWidth: 90, textAlign: 'center' }}>
              <Typography variant="h6">{dashboard.data?.byStatus[s] ?? 0}</Typography>
              <Typography variant="caption">{s}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={2} mb={2}>
        <TextField size="small" placeholder="Search OMS ID / title" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="View / workflow"><IconButton size="small" onClick={() => setDetail(r)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('oms.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('oms.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <OmsFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <OmsDetailDialog oms={detail} onClose={() => setDetail(null)} onChanged={invalidate} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete OMS"
        message={`Soft-delete ${deleting?.omsNumber}? Blocked if transactions are linked.`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
